import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authLogout, authMe, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  isAdmin: false,
  isViewer: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await authMe();
      setUser(data?.user || null);
      return data?.user || null;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: coming back from OAuth - AuthCallback exchanges the session_id first,
    // otherwise /auth/me would 401 before the cookie exists.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (e) {
      /* the cookie is cleared client-side anyway */
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAdmin: (user?.role || "") === "admin",
      isViewer: (user?.role || "") === "viewer",
      setUser,
      refresh,
      login,
      logout,
    }),
    [user, loading, refresh, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
